import { api } from './baseAPI';
import {
  ApiResponse,
  LessonRequest,
  LessonApplicationRequest,
  LessonApprovalRequest,
  PracticeRoom,
  PracticeBooking,
  PracticeBookingRequest,
  LessonSchedule,
  LessonScheduleRequest,
  ListResponse,
  OneDayRequest,
  OneDayApplicationRequest
} from '../types/api';

// 수업 신청 관련 API
export const lessonAPI = {
  // 수업 신청
  apply: async (application: LessonApplicationRequest): Promise<ApiResponse<LessonRequest>> => {
    const response = await api.post('/lessons/apply', application);
    return response.data;
  },

  // 수업 신청 목록 조회
  getRequests: async (params?: { status?: string; page?: number; limit?: number; offset?: number; searchField?: string }): Promise<ApiResponse<ListResponse<LessonRequest>>> => {
    const response = await api.get('/lessons/requests', { params });
    return response.data;
  },

  // 수업 신청 상세 조회
  getRequest: async (requestId: string): Promise<ApiResponse<LessonRequest>> => {
    const response = await api.get(`/lessons/requests/${requestId}`);
    return response.data;
  },

  // 수업 신청 승인
  approveRequest: async (requestId: string, approvalData: LessonApprovalRequest): Promise<ApiResponse<any>> => {
    const response = await api.patch(`/lessons/requests/${requestId}/approve`, approvalData);
    return response.data;
  },

  // 수업 신청 거절
  rejectRequest: async (requestId: string, reason: string): Promise<ApiResponse<any>> => {
    const response = await api.patch(`/lessons/requests/${requestId}/reject`, { reason });
    return response.data;
  },
};

// 연습실 관련 API
export const practiceAPI = {
  // 연습실 목록 조회
  getRooms: async (): Promise<ApiResponse<PracticeRoom[]>> => {
    const response = await api.get('/practice/rooms');
    return response.data;
  },

  // 연습실 상세 조회
  getRoom: async (roomId: number): Promise<ApiResponse<PracticeRoom>> => {
    const response = await api.get(`/practice/rooms/${roomId}`);
    return response.data;
  },

  // 연습실 예약 가능 시간 조회
  getAvailableSlots: async (roomId: number, date: string): Promise<ApiResponse<any>> => {
    const response = await api.get(`/practice/rooms/${roomId}/available-slots`, {
      params: { date }
    });
    return response.data;
  },

  // 연습실 예약
  bookRoom: async (bookingData: PracticeBookingRequest): Promise<ApiResponse<PracticeBooking>> => {
    const response = await api.post('/practice/bookings', bookingData);
    return response.data;
  },

  // 예약 목록 조회
  getBookings: async (params?: {
    status?: string;
    page?: number;
    limit?: number;
    startDate?: string;
    endDate?: string;
  }): Promise<ApiResponse<ListResponse<PracticeBooking>>> => {
    const response = await api.get('/practice/bookings', { params });
    return response.data;
  },

  // 예약 상세 조회
  getBooking: async (bookingId: string): Promise<ApiResponse<PracticeBooking>> => {
    const response = await api.get(`/practice/bookings/${bookingId}`);
    return response.data;
  },

  // 예약 취소
  cancelBooking: async (sessionId: string): Promise<ApiResponse<any>> => {
    const response = await api.put(`/practice/my-sessions/${sessionId}/cancel`);
    return response.data;
  },

  // 모든 연습실 예약 조회 (선생님용)
  getAllPracticeSessions: async (params?: {
    status?: string;
    page?: number;
    limit?: number;
    startDate?: string;
    endDate?: string;
  }): Promise<ApiResponse<ListResponse<PracticeBooking>>> => {
    const response = await api.get('/practice', { params });
    return response.data;
  },
};

// 수업 일정 관련 API
export const scheduleAPI = {
  // 수업 일정 생성
  createSchedule: async (scheduleData: LessonScheduleRequest): Promise<ApiResponse<LessonSchedule>> => {
    const response = await api.post('/schedule', scheduleData);
    return response.data;
  },

  // 수업 일정 목록 조회
  getSchedules: async (params?: {
    teacherId?: number;
    userId?: number;
    status?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<ListResponse<LessonSchedule>>> => {
    // teacherId가 없으면 기본값 1 사용
    const finalParams = { ...params, teacherId: params?.teacherId || 1 };
    const response = await api.get('/schedule', { params: finalParams });
    return response.data;
  },

  // 수업 일정 상세 조회
  getSchedule: async (scheduleId: string): Promise<ApiResponse<LessonSchedule>> => {
    const response = await api.get(`/schedule/${scheduleId}`);
    return response.data;
  },

  // 수업 일정 수정
  updateSchedule: async (scheduleId: string, scheduleData: Partial<LessonScheduleRequest>): Promise<ApiResponse<LessonSchedule>> => {
    const response = await api.patch(`/schedule/${scheduleId}`, scheduleData);
    return response.data;
  },

  // 수업 일정 삭제
  deleteSchedule: async (scheduleId: string): Promise<ApiResponse<any>> => {
    const response = await api.delete(`/schedule/${scheduleId}`);
    return response.data;
  },

  // 특정 요일의 정기 수업 시간대 조회
  getTeacherBusyTimes: async (params: {
    dayOfWeek: string;
    teacherId?: string;
  }): Promise<ApiResponse<{
    dayOfWeek: string;
    busyTimes: Array<{
      startTime: string;
      endTime: string;
      studentName?: string;
      type: string;
    }>;
    totalCount: number;
  }>> => {
    // teacherId가 없으면 기본값 1 사용
    const finalParams = { ...params, teacherId: params?.teacherId || '1' };
    const response = await api.get('/schedule/teacher-busy-times', { params: finalParams });
    return response.data;
  },

  // 선생님의 모든 요일 정규 수업 시간대 조회 (새로운 API)
  getAllTeacherBusyTimes: async (params?: {
    teacherId?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<ApiResponse<{
    [key: string]: {
      dayOfWeek: string;
      busyTimes: Array<{
        startTime: string;
        endTime: string;
        studentName?: string;
        type: string;
      }>;
      totalCount: number;
    };
  }>> => {
    // teacherId가 없으면 기본값 1 사용
    const finalParams = { ...params, teacherId: params?.teacherId || '1' };
    const response = await api.get('/schedule/all-teacher-busy-times', { params: finalParams });
    return response.data;
  },

  // 수업 완료 처리
  completeSchedule: async (scheduleId: string, notes?: string): Promise<ApiResponse<LessonSchedule>> => {
    const response = await api.patch(`/schedule/${scheduleId}/complete`, { notes });
    return response.data;
  },

  // 캘린더 이벤트 조회
  getCalendarEvents: async (params: {
    startDate: string;
    endDate: string;
    teacherId?: number;
    userId?: number;
  }): Promise<ApiResponse<any>> => {
    const response = await api.get('/schedule/calendar', { params });
    return response.data;
  },

  // 사용자의 수업 일정 오버라이드 조회
  getUserScheduleOverrides: async (): Promise<ApiResponse<{
    overrides: Array<{
      startDate: string;
      endDate: string;
      newDate: string;
      scope: string;
      type: string;
      newStartTime: string;
      newEndTime: string;
    }>;
  }>> => {
    const response = await api.get('/schedule/my-overrides');
    return response.data;
  },
};



// 원데이클래스 관련 API
export const oneDayAPI = {
  // 원데이클래스 신청
  apply: async (application: OneDayApplicationRequest): Promise<ApiResponse<OneDayRequest>> => {
    const response = await api.post('/oneday/apply', application);
    return response.data;
  },

  // 원데이클래스 신청 목록 조회 (관리자용)
  getRequests: async (params?: {
    status?: string;
    page?: number;
    limit?: number;
    offset?: number;
    searchField?: string;
  }): Promise<ApiResponse<ListResponse<OneDayRequest>>> => {
    const response = await api.get('/oneday/requests', { params });
    return response.data;
  },

  // 원데이클래스 신청 상태 업데이트 (관리자용)
  updateStatus: async (requestId: string, updateData: {
    status: string;
    scheduledAt?: string;
    handledByTeacher?: number;
  }): Promise<ApiResponse<OneDayRequest>> => {
    const response = await api.patch(`/oneday/requests/${requestId}`, updateData);
    return response.data;
  }
};

export default lessonAPI;